import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Copy, Trash2, RefreshCcw, KeyRound, ShieldAlert, ExternalLink } from "lucide-react";
import { PageHeader } from "@/pages/Accounts";

const ALL_SCOPES = [
  { id: "campaign:create", desc: "Create draft campaigns" },
  { id: "campaign:send", desc: "Start/send campaigns" },
  { id: "campaign:read", desc: "Read campaign detail + progress + reports" },
  { id: "contact_list:read", desc: "List contact lists + facets" },
  { id: "contact_list:write", desc: "Upload new contact lists (not recommended for external)" },
  { id: "template:read", desc: "Read DLT templates" },
  { id: "dlr:poll", desc: "Trigger DLR poll manually" },
];

export default function ApiKeysPage() {
  const { id: accountId } = useParams();
  const [keys, setKeys] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState({ name: "", scopes: [] });
  const [freshKey, setFreshKey] = useState(null); // shows one-time raw key

  const load = async () => {
    try {
      const { data } = await api.get(`/accounts/${accountId}/api-keys`);
      setKeys(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountId]);

  const create = async () => {
    if (!newKey.name.trim() || !newKey.scopes.length) return;
    try {
      const { data } = await api.post(`/accounts/${accountId}/api-keys`, newKey);
      setFreshKey(data);
      setCreating(false);
      setNewKey({ name: "", scopes: [] });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/accounts/${accountId}/api-keys/${id}`);
      toast.success("Key revoked");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const rotate = async (id) => {
    try {
      const { data } = await api.post(`/accounts/${accountId}/api-keys/${id}/rotate`);
      setFreshKey(data);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleScope = (s) => {
    setNewKey({
      ...newKey,
      scopes: newKey.scopes.includes(s)
        ? newKey.scopes.filter(x => x !== s)
        : [...newKey.scopes, s],
    });
  };

  const copy = (v) => {
    navigator.clipboard.writeText(v);
    toast.success("Copied");
  };

  return (
    <div className="p-8 space-y-6">
      <Link to={`/accounts/${accountId}`} className="text-xs text-muted-foreground hover:underline inline-flex items-center gap-1">
        <ArrowLeft className="h-3 w-3" /> Back to account
      </Link>
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title="API Keys"
          description="Programmatic access for this account. Keys are hashed at rest — the raw key is shown ONCE at creation."
        />
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" data-testid="apikeys-docs-link">
            <a href="/api/docs" target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-3 w-3 mr-1" />API Docs
            </a>
          </Button>
          <Button onClick={() => setCreating(true)} data-testid="apikeys-create-btn">
            <Plus className="h-4 w-4 mr-1" />New key
          </Button>
        </div>
      </div>

      <Card data-testid="apikeys-list-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <KeyRound className="h-4 w-4" /> Keys
          </CardTitle>
          <CardDescription>All API keys ever issued for this account. Revoked keys stay in the list for audit.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Prefix</TableHead>
                <TableHead>Scopes</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys === null && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-xs">Loading…</TableCell></TableRow>
              )}
              {keys?.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-xs text-muted-foreground">No API keys yet. Click "New key" to create one.</TableCell></TableRow>
              )}
              {keys?.map(k => (
                <TableRow key={k.id} data-testid={`apikey-row-${k.id}`}>
                  <TableCell className="text-sm">{k.name}</TableCell>
                  <TableCell className="font-mono text-[11px]">{k.key_prefix}</TableCell>
                  <TableCell className="max-w-md">
                    <div className="flex flex-wrap gap-1">
                      {k.scopes.map(s => (
                        <Badge key={s} variant="outline" className="text-[9px] font-mono">{s}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-[11px]">
                    {k.last_used_at ? k.last_used_at.replace("T", " ").slice(0, 16) : "never"}
                  </TableCell>
                  <TableCell>
                    {k.is_active
                      ? <Badge variant="default" className="text-[10px]">active</Badge>
                      : <Badge variant="secondary" className="text-[10px]">revoked</Badge>}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    {k.is_active && (
                      <>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`apikey-rotate-${k.id}`}>
                              <RefreshCcw className="h-3 w-3 mr-1" />Rotate
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Rotate this key?</AlertDialogTitle>
                              <AlertDialogDescription>
                                The old key is revoked immediately and a new key is issued with the same name and scopes. Any live integration using the old key will start returning 401.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => rotate(k.id)} data-testid={`apikey-rotate-confirm-${k.id}`}>Rotate now</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`apikey-revoke-${k.id}`}>
                              <Trash2 className="h-3 w-3 mr-1" />Revoke
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Revoke this key?</AlertDialogTitle>
                              <AlertDialogDescription>
                                All requests using this key will start returning 401 within seconds. This action cannot be undone; you can create a new key later.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => revoke(k.id)} data-testid={`apikey-revoke-confirm-${k.id}`}>Revoke</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="apikey-create-dialog">
          <DialogHeader>
            <DialogTitle>Create API key</DialogTitle>
            <DialogDescription>Pick a name and the scopes this key should have.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Name</Label>
              <Input
                value={newKey.name}
                onChange={(e) => setNewKey({ ...newKey, name: e.target.value })}
                placeholder="e.g. AdmissionCraft webapp"
                data-testid="apikey-new-name"
              />
            </div>
            <div>
              <Label className="text-xs mb-2 block">Scopes</Label>
              <div className="space-y-2">
                {ALL_SCOPES.map(s => (
                  <label key={s.id} className="flex items-start gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={newKey.scopes.includes(s.id)}
                      onCheckedChange={() => toggleScope(s.id)}
                      data-testid={`apikey-scope-${s.id}`}
                    />
                    <span>
                      <span className="font-mono text-xs">{s.id}</span>
                      <span className="text-xs text-muted-foreground ml-2">— {s.desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
            <Button
              onClick={create}
              disabled={!newKey.name.trim() || !newKey.scopes.length}
              data-testid="apikey-create-submit"
            >
              Create key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One-time raw key display */}
      <Dialog open={!!freshKey} onOpenChange={(o) => !o && setFreshKey(null)}>
        <DialogContent data-testid="apikey-freshkey-dialog">
          <DialogHeader>
            <DialogTitle>Your API key</DialogTitle>
            <DialogDescription>
              This is the ONLY time you will see the full key. Copy it now and store it securely (env var / secrets manager). We only keep a hash of it — we cannot show it again.
            </DialogDescription>
          </DialogHeader>
          <Alert variant="destructive">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>Copy now — we won&apos;t show it again</AlertTitle>
            <AlertDescription className="text-xs">
              If you close this dialog without copying, you&apos;ll need to rotate the key to get a new one.
            </AlertDescription>
          </Alert>
          {freshKey && (
            <div className="space-y-2">
              <Label className="text-xs">API key ({freshKey.name})</Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={freshKey.key}
                  className="font-mono text-xs"
                  data-testid="apikey-freshkey-value"
                />
                <Button size="sm" onClick={() => copy(freshKey.key)} data-testid="apikey-freshkey-copy">
                  <Copy className="h-3 w-3 mr-1" />Copy
                </Button>
              </div>
              <div className="flex flex-wrap gap-1 pt-2">
                {(freshKey.scopes || []).map(s => (
                  <Badge key={s} variant="outline" className="text-[9px] font-mono">{s}</Badge>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFreshKey(null)} data-testid="apikey-freshkey-close">I&apos;ve saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
